import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, EMPTY, Subject, take, takeUntil } from 'rxjs';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { PrintAttributeDefinition, PrintAttributeType } from '../../../models/print-attribute.model';
import { EventAssociatedPrintAttributeService } from '../../../services/event-associated-print-attribute.service';
import { PrintAttributeService } from '../../../services/print-attribute.service';
import * as dayjs from 'dayjs';

export interface EditEventAssociatedAttributeDialogData {
  eventId: string;
  printAttributeId: string;
  printAttributeDefinition: PrintAttributeDefinition;
}

interface DynamicFieldDef {
  id: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'url' | 'textarea';
  placeholder?: string;
}

@Component({
  selector: 'app-edit-event-associated-attribute-dialog',
  templateUrl: './edit-event-associated-attribute-dialog.component.html',
})
export class EditEventAssociatedAttributeDialogComponent implements OnInit {
  private readonly dynamicFieldDefs: Record<string, DynamicFieldDef[]> = {
    'address': [{ id: 'addressText', label: 'Store Address', type: 'text' }],
    'sale_dates': [
      { id: 'saleStart', label: 'Sale Start Date', type: 'date' },
      { id: 'saleEnd', label: 'Sale End Date', type: 'date' },
    ],
    'url': [
      { id: 'assetId', label: 'Asset ID', type: 'text' },
      { id: 'urlValue', label: 'URL', type: 'url', placeholder: 'https://example.com' },
    ],
    'printer_code': [
      { id: 'printerCode', label: 'Printer Code', type: 'text' },
      { id: 'routingDest', label: 'Routing Destination', type: 'text' },
    ],
    'header': [
      { id: 'assetId', label: 'Asset ID', type: 'text' },
      { id: 'urlValue', label: 'URL', type: 'url', placeholder: 'https://example.com' },
      { id: 'headerText', label: 'Header Text', type: 'text' },
      { id: 'headerSubtext', label: 'Subheader Text', type: 'text' },
    ],
    'creative': [
      { id: 'assetId', label: 'Asset ID', type: 'text' },
      { id: 'urlValue', label: 'URL', type: 'url', placeholder: 'https://example.com' },
      { id: 'creativeTitle', label: 'Creative Title', type: 'text' },
      { id: 'placementNotes', label: 'Placement Notes', type: 'text' },
    ],
    'market_place': [
      { id: 'assetId', label: 'Asset ID', type: 'text' },
      { id: 'urlValue', label: 'URL', type: 'url', placeholder: 'https://example.com' },
      { id: 'marketplaceTitle', label: 'Marketplace Title', type: 'text' },
      { id: 'promoBlock', label: 'Promo Block Content', type: 'textarea' },
    ],
    'offer_event': [
      { id: 'offerTitle', label: 'Event Label', type: 'text' },
      { id: 'brandOverlay', label: 'Brand Overlay Text', type: 'text' },
    ],
    'abev': [
      { id: 'assetId', label: 'Asset ID', type: 'text' },
      { id: 'urlValue', label: 'URL', type: 'url', placeholder: 'https://example.com' },
      { id: 'abevTitle', label: 'License Type', type: 'text' },
      { id: 'abevNotes', label: 'ABEV Layout Notes', type: 'textarea' },
    ],
    'dry_version': [
      { id: 'dryVersionTitle', label: 'Replacement Content', type: 'text' },
      { id: 'dryVersionNotes', label: 'Layout Notes', type: 'textarea' },
    ],
  };

  dynamicFields: DynamicFieldDef[] = [];

  readonly metaForm = new FormGroup({
    printAttributeName: new FormControl({ value: '', disabled: true }),
    printAttributeType: new FormControl({ value: '', disabled: true }),
    eventTypeName: new FormControl({ value: '', disabled: true }),
    divisionNames: new FormControl({ value: '', disabled: true }),
    description: new FormControl({ value: '', disabled: true }),
    versions: new FormControl({ value: '', disabled: true }),
  });

  customFieldsForm = new FormGroup({});

  private printAttributeDefinition!: PrintAttributeDefinition;

  constructor(
    private dialogRef: MatDialogRef<EditEventAssociatedAttributeDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: EditEventAssociatedAttributeDialogData,
    private associatedService: EventAssociatedPrintAttributeService,
    private printAttributeService: PrintAttributeService,
    private snackBar: MatSnackBar,
  ) {
    this.printAttributeDefinition = this.data.printAttributeDefinition;
  }

  ngOnInit(): void {
    this.applyPrintAttributeDefinition(this.printAttributeDefinition);
  }

  save(): void {
    const raw = this.customFieldsForm.getRawValue() as Record<string, unknown>;
    const payload = this.normalizeValuesForApi(raw);

    this.associatedService
      .createEventAssociation(this.data.eventId, this.data.printAttributeId, payload)
      .subscribe({
        next: () => {
          this.snackBar.open(`${this.printAttributeDefinition.Detail?.PrintAttributeName} values updated successfully.`, 'OK', DEFAULT_SNACKBAR_CONFIG);
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackBar.open('Could not save values', 'OK', DEFAULT_SNACKBAR_CONFIG);
        },
      });
  }

  private applyPrintAttributeDefinition(printAttributeDefinition: PrintAttributeDefinition): void {
    const detail = printAttributeDefinition.Detail ?? {};
    const versions =
      Array.isArray(detail.LogicalVersions) && detail.LogicalVersions.length
        ? detail.LogicalVersions.join(', ')
        : '';

    this.metaForm.patchValue({
      printAttributeName: detail.PrintAttributeName || '',
      printAttributeType: detail.PrintAttributeType || '',
      eventTypeName: printAttributeDefinition.EventTypeName || '',
      divisionNames: printAttributeDefinition.DivisionNames || '',
      description: detail.Description || '',
      versions,
    });

    const attrType = detail.PrintAttributeType || '';
    this.dynamicFields = this.dynamicFieldDefs[attrType] ?? [];

    Object.keys(this.customFieldsForm.controls).forEach((key) =>
      this.customFieldsForm.removeControl(key),
    );

    this.dynamicFields.forEach((field) => {
      this.customFieldsForm.addControl(
        field.id,
        new FormControl(field.type === 'date' ? null : ''),
      );
    });

    const existing = this.parseExistingDynamicValues(printAttributeDefinition);
    this.dynamicFields.forEach((field) => {
      this.customFieldsForm
        .get(field.id)
        ?.setValue(this.coerceForForm(field, existing[field.id]));
    });
  }

  private parseExistingDynamicValues(printAttributeDefinition: PrintAttributeDefinition): Record<string, unknown> {
    const custom = { ...(printAttributeDefinition.Data ?? {}) } as Record<string, unknown>;

    let jsonSource: string | undefined;
    if (printAttributeDefinition.Data != null && String(printAttributeDefinition.Data).trim()) {
      jsonSource = String(printAttributeDefinition.Data);
    }

    try {
      const o = JSON.parse(jsonSource ?? '{}');
      if (o && typeof o === 'object' && !Array.isArray(o)) {
        return { ...custom, ...(o as Record<string, unknown>) };
      }
    } catch {
      /* ignore invalid JSON */
    }
    return custom;
  }

  private coerceForForm(field: DynamicFieldDef, value: unknown): unknown {
    if (field.type !== 'date') {
      return value ?? '';
    }
    if (value == null || value === '') {
      return null;
    }
    const d = value instanceof Date ? value : new Date(String(value));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private normalizeValuesForApi(values: Record<string, unknown>): Record<string, string> {
    const out: Record<string, string> = {};
    this.dynamicFields.forEach((field) => {
      const raw = values[field.id];
      if (field.type === 'date') {
        out[field.id] = this.toMmDdYyyy(raw);
        return;
      }
      if (raw == null) {
        out[field.id] = '';
        return;
      }
      out[field.id] = String(raw);
    });
    return out;
  }

  private toMmDdYyyy(value: unknown): string {
    if (!value) {
      return '';
    }
    const parsedDate = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(parsedDate.getTime())) {
      return String(value);
    }
    return dayjs(parsedDate).format('MM/DD/YYYY');
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
